/**
 * WEB-1648 (условие узаконивания №3): подсказка на погашенной ДЕНЕЖНОЙ
 * кнопке, когда подставщик проекта не нашёл адреса.
 *
 * Крайние случаи подставщика — у организации ноль проектов, единственный
 * проект удалён, человек в нескольких организациях и активная пуста — до
 * карточки давали молча мёртвую кнопку: нажать нельзя, почему — не сказано.
 * Требование владельца: «Что происходит с денежной кнопкой в каждом — должно
 * быть определено, а не "как выйдет"». Определено так: погашена + причина.
 *
 * Две тонкости, обе намеренные:
 *  1. Обёртка `<span>` обязательна, а не украшение: погашенная `<button>` не
 *     отдаёт события мыши, и подсказка, повешенная прямо на неё, не всплывёт
 *     НИКОГДА. Канон repo — OrgProjects.tsx «Создать проект» (там это
 *     проверено живым прогоном на этой же версии antd).
 *  2. Когда адрес ЕСТЬ, обёртки нет вовсе — разметка рабочего пути остаётся
 *     байт-в-байт прежней, и ни одна живая денежная кнопка не получает
 *     лишнего слоя между собой и человеком.
 *
 * Ревизия пачки 9 (п. М11): `inline-block` сжимается по содержимому, поэтому
 * кнопки `<Button block>` (PricingModal, TopUpPackages) в состоянии «адреса
 * нет» съёживались до ширины текста — на ту самую ширину, которой карточка
 * тарифа и не рассчитана. Для них у обёртки свой режим: `display: block` +
 * `width: 100%`. Признак задаётся ЯВНО (`block`), а не угадывается по детям:
 * гадание по вложенному элементу — это второе объявление того же правила.
 */
import React from 'react';
import { Tooltip } from 'antd';
import { useStrings } from '@/i18n';
import { billingStrings } from '@/i18n/dictionaries/billing';

interface MoneyActionHintProps {
  /** Подставщик нашёл адрес проекта (кнопка живая) — подсказка не нужна. */
  readonly hasProjectId: boolean;
  /** testid обёртки: `<testid кнопки>-wrap` (наводить надо на неё). */
  readonly wrapTestId: string;
  /**
   * Ребёнок — кнопка во всю ширину (`<Button block>`). Обёртка тогда тоже во
   * всю ширину, иначе кнопка схлопывается по тексту (М11).
   */
  readonly block?: boolean;
  readonly children: React.ReactNode;
}

export default function MoneyActionHint({
  hasProjectId,
  wrapTestId,
  block = false,
  children,
}: MoneyActionHintProps) {
  const s = useStrings(billingStrings);
  if (hasProjectId) return <>{children}</>;

  return (
    <Tooltip title={s.noBillingProjectHint}>
      <span
        style={
          block
            ? { display: 'block', width: '100%' }
            : { display: 'inline-block' }
        }
        data-testid={wrapTestId}
      >
        {children}
      </span>
    </Tooltip>
  );
}
